/**
 * A farm win, as money that has left a balance.
 *
 * Tim, 9 September: "FAAB should update immediately and be recorded to ledger when bid is complete
 * (and undo if needed, too)."
 *
 * THE HOLE WAS THE SAME ONE THE TRADES HAD. `FaabEntry.kind` has carried `'farm-bid'` since the
 * type was written and nothing ever wrote one, so a lot sold for ₣35 left the winner's balance
 * exactly where it was until a commissioner remembered to type a correction. The bid table's
 * "₣128 left" was therefore last night's figure all evening, in front of the man deciding whether
 * he could afford the next raise — 2025-proposal-01-faab-farm-bids §1 forbids a bid he cannot
 * cover, and the screen was showing him a number that said he could.
 *
 * THE ID IS THE TURN, which is what makes the hammer safe to press twice: `farm-2026-1.02` computed
 * twice is the same document twice, and two clients hammering the same lot write one row. It also
 * makes the undo exact — the row to remove is derived from the turn rather than searched for by
 * amount, so undoing a ₣12 sale cannot take somebody else's ₣12 off the ledger.
 */

import { faab } from './faab.js';
import { nomCode } from './farmAuction.js';
import type { FaabEntry, FarmNomination, GmId } from '../store/types.js';

/** Every farm-auction row is this kind, and nothing else writes it. */
export const FARM_KIND = 'farm-bid' as const;

/** `farm-2026-1.02` — the season and the turn, so a lot has exactly one row for its whole life. */
export function farmSpendId(season: number, round: number, slot: number): string {
  return `farm-${season}-${nomCode(round, slot)}`;
}

/**
 * The ledger row for a sale, or null if the record is not a sale.
 *
 * The date is TODAY rather than the nomination's `updatedAt`: a farm auction happens in one evening
 * and the row belongs on the day the room was in it, which is the day the ledger groups it under.
 */
export function farmSpendEntry(
  n: FarmNomination,
  by: GmId,
  at: string,
): FaabEntry | null {
  if (!n.winner || n.bid == null || !n.player) return null;
  return {
    id: farmSpendId(n.season, n.round, n.slot),
    season: n.season,
    gm: n.winner.toUpperCase() as GmId,
    /* Negative is a spend, as everywhere else in this ledger. */
    amount: -Math.abs(Math.round(n.bid)),
    kind: FARM_KIND,
    description: `${n.player} at ${nomCode(n.round, n.slot)} for ${faab(n.bid)}`,
    date: at.slice(0, 10),
    recordedBy: by,
  };
}
